import {
  BRAND_HAS_MIDNIGHT_SHOWTIMES,
  RELEASE_WEEK_DAY
} from 'common/configuration/constants';
import formatDate, { HOURS_FORMATS } from 'common/tools/date/format';

export type DateProcessingFromAString = {
  date: Date;
  hour: string;
  isPast: boolean;
  originalString: string;
  time: number;
};

const DAY: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7
};

const getShowtimeReleaseWeekDay = () => RELEASE_WEEK_DAY.toLowerCase();

/**
 * @returns {number} the next week day time by calculating the number of days until it happens and adding it to now
 */
export const getNextWeekStartTime = () => {
  const releaseWeekDay = DAY[getShowtimeReleaseWeekDay()];
  const now = new Date();
  const day = now.getDay();
  const date = now.getDate();
  const month = now.getMonth();
  const year = now.getFullYear();

  const nextWeekDayIn =
    day > releaseWeekDay ? 7 - (day - releaseWeekDay) : releaseWeekDay - day;

  return (
    new Date(year, month, date).getTime() + nextWeekDayIn * 24 * 60 * 60 * 1000
  );
};

export { getShowtimeReleaseWeekDay as getShowtimeReleaseWeekDayForTest };

// WARNING : issue on safari with new Date only date, we can't use new Date(stringDate);
// "fix" found on https://stackoverflow.com/questions/6427204/date-parsing-in-javascript-is-different-between-safari-and-chrome
const parseShowtimeDate = (stringDate: string): Date => {
  const dateSplit = stringDate.split(/[^0-9]/);

  return new Date(
    parseInt(dateSplit[0], 10),
    parseInt(dateSplit[1], 10) - 1,
    parseInt(dateSplit[2], 10),
    parseInt(dateSplit[3], 10),
    parseInt(dateSplit[4], 10),
    parseInt(dateSplit[5], 10)
  );
};

export const dateProcessingFromAString = (stringDate: string) => {
  const date = parseShowtimeDate(stringDate);

  const hour: string = formatDate(date, { ...HOURS_FORMATS.SHORT });

  const time = date.getTime();

  const isPast = time < Date.now();

  return {
    date,
    hour,
    isPast,
    originalString: stringDate,
    time
  };
};

// a showtime starting before this hour is a "séance de minuit" (the theater day starts at 03:00)
const MIDNIGHT_END_HOUR = 3;

/**
 * A showtime starting between 00:00 and 02:59 belongs to the previous "theater day" (ex: a 25/02
 * 00:10 showtime is a "Séance de minuit" displayed under 24/02). See ALLOCINE-15181.
 */
export const getShowtimeDisplayDay = (
  stringDate: string
): { displayDate: Date; isMidnight: boolean } => {
  const date = parseShowtimeDate(stringDate);
  // brands without the feature keep every showtime under its own day, as before
  const isMidnight =
    BRAND_HAS_MIDNIGHT_SHOWTIMES && date.getHours() < MIDNIGHT_END_HOUR;
  const displayDate = isMidnight
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1)
    : new Date(date.getFullYear(), date.getMonth(), date.getDate());

  return { displayDate, isMidnight };
};

// Formats a Date's local calendar day as 'YYYY-MM-DD', the shape used for the "currentDay"/"day"
// route params throughout the showtimes pages. Comparing day strings avoids the timezone pitfalls
// of comparing Date instances built from mixed UTC/local components.
export const formatDayString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Whether a showtime is listed under the given day. A midnight showtime is listed twice (see
 * ALLOCINE-15181): under its own calendar day (J, the night that ends on it) and under the day
 * before (J-1, the night that starts on it).
 */
export const isShownOnDay = (startsAt: string, day: Date): boolean => {
  const { displayDate, isMidnight } = getShowtimeDisplayDay(startsAt);
  const dayString = formatDayString(day);

  return (
    formatDayString(parseShowtimeDate(startsAt)) === dayString ||
    (isMidnight && formatDayString(displayDate) === dayString)
  );
};

/**
 * Last moment to request when listing the showtimes of a day: 02:59:59 the next morning, so the
 * midnight showtimes listed under that day (J-1) are fetched too.
 */
export const getEndOfShowtimeDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23 + MIDNIGHT_END_HOUR, 59, 59);
  return result;
};
